import React, { useMemo } from 'react';
import Chip from '@material-ui/core/Chip';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Tooltip from '@material-ui/core/Tooltip';
import TextField from '@material-ui/core/TextField';

// Normalize option -> safe strings for key/compare
function normalizeOption(opt, idx) {
  if (!opt || (opt.value == null && opt.label == null)) {
    return {
      value: `__invalid_${idx}__`,
      label: '',
      color: undefined,
      icon: undefined,
      __invalid: true,
    };
  }
  const raw = opt.value != null ? opt.value : opt.label;
  return {
    value: String(raw),
    label: String(opt.label != null ? opt.label : raw),
    color: opt.color,
    icon: opt.icon,
  };
}

// Accept ElementType or Element
function renderIcon(icon, color) {
  if (!icon) return null;
  if (React.isValidElement(icon)) {
    return React.cloneElement(icon, {
      style: {
        ...(icon.props.style || {}),
        color,
      },
      htmlColor: color,
    });
  }
  if (typeof icon === 'function') {
    const Icon = icon;
    return (
      <Icon
        style={{ color }}
        htmlColor={color}
      />
    );
  }
  return null;
}

export default function StatusDropdown({
  value = null,
  options = [],
  onChange = () => {},
  variant = 'chip',
  triggerIcon,
  ariaLabel = 'Selecionar',
  showSearch = true,
  showSelectedHeader = false,
  headerLabel = '',
  isOpen = true,
  menuContainer = null,
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const triggerRef = React.useRef(null);
  const [search, setSearch] = React.useState('');

  const resolvedContainer = useMemo(() => {
    if (typeof menuContainer === 'function') return menuContainer;
    if (menuContainer) return () => menuContainer;
    // fallback (no context dependency)
    return () => document.body;
  }, [menuContainer]);

  const mode = variant === 'icon' ? 'icon' : 'chip';

  const normalized = options.map((opt, idx) => normalizeOption(opt, idx));

  const valueStr = value == null ? null : String(value);
  const selected = valueStr == null ? null : normalized.find(o => o.value === valueStr);
  const selectedColor = selected?.color;

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(triggerRef.current);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setSearch('');
    setAnchorEl(null);
  };

  const handleSelect = (valStr) => {
    const original = options.find(o => String(o.value) === valStr);
    onChange(original ? original.value : valStr);
    setSearch('');
    setAnchorEl(null);
  };

  const lc = s => (s || '').toLowerCase();

  const filtered = search
    ? normalized.filter(o => lc(o.label).includes(lc(search)))
    : normalized;

  const chipStyle = {
    background: selectedColor || '#9e9e9e',
    color: '#fff',
    height: 28,
    borderRadius: 14,
    fontWeight: 700,
    cursor: 'pointer',
    maxWidth: '105px',
  };

  const iconButtonStyle = {
    color: selectedColor,
    borderRadius: 14,
    padding: 4,
  };

  const headerWrap = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    width: 260,
    background: 'rgba(0,0,0,0.06)',
  };

  const dotStyle = color => ({
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: color || '#9e9e9e',
    display: 'inline-block',
  });

  const stop = e => e.stopPropagation();

  return (
    <>
      {mode === 'icon' ? (
        <Tooltip title={ariaLabel}>
          <IconButton
            ref={triggerRef}
            aria-label={ariaLabel}
            aria-haspopup="menu"
            aria-controls={anchorEl ? 'status-dropdown-menu' : undefined}
            aria-expanded={anchorEl ? 'true' : undefined}
            onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
            onClick={handleOpen}
            disableRipple
            size="small"
            style={iconButtonStyle}
          >
            {renderIcon(triggerIcon)}
          </IconButton>
        </Tooltip>
      ) : (
        <Chip
          ref={triggerRef}
          onClick={handleOpen}
          label={selected ? selected.label : ''}
          style={chipStyle}
        />
      )}

      <Menu
        id="status-dropdown-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        container={resolvedContainer}
        disablePortal={false}
        SelectProps={{
          MenuListProps: { autoFocusItem: false },
        }}
      >
        {showSelectedHeader && (
          <div key="hdr" style={headerWrap}>
            <ListItemIcon style={{ minWidth: 28 }}>
              {selected?.icon
                ? renderIcon(selected.icon, selected.color)
                : <span style={dotStyle(selected?.color)} />
              }
            </ListItemIcon>
            <ListItemText
              primary={selected ? selected.label : headerLabel}
              primaryTypographyProps={{
                style: {
                  fontWeight: 700,
                  color: 'rgba(0,0,0,0.6)',
                },
              }}
            />
          </div>
        )}

        {showSearch && (
          <div
            key="hrr"
            style={{
              padding: 8,
              width: 260,
            }}
          >
            <TextField
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar…"
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{ style: { height: 34 } }}
              onMouseDown={stop}
              onClick={stop}
              onKeyDown={(e) => {
                stop(e);
                if (e.key === 'Escape') handleClose(e);
              }}
            />
          </div>
        )}

        <div>
          {filtered.map((opt) => {
            const isSelected = opt.value === valueStr;
            return (
              <MenuItem
                key={opt.value}
                selected={isSelected}
                onClick={(e) => {
                  stop(e);
                  handleSelect(opt.value);
                }}
              >
                <ListItemIcon style={{ minWidth: 28 }}>
                  {opt.icon
                    ? renderIcon(opt.icon, opt.color)
                    : <span style={dotStyle(opt.color)} />
                  }
                </ListItemIcon>
                <ListItemText primary={opt.label} />
              </MenuItem>
            );
          })}
        </div>
      </Menu>
    </>
  );
}
